import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'
import { useForm, useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'

import Errors from '@tandem/ui/Errors'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC } from '@/trpc'

import $layout from '@tandem/ui/layout.css'
import $type from '@tandem/ui/type.css'
import $anim from '@tandem/ui/anim.css'

import SplashLayout from '@/layouts/Splash'

import ArrowRightCircleFillIcon from '@tandem/ui/icons/arrow.right.circle.fill.svg?react'
import $ from './-styles.css'

export const Route = createFileRoute('/login/')({
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) || undefined,
  }),
  beforeLoad: ({ context }) => {
    if (context.auth.isAuthenticated) {
      throw redirect({ to: '/' })
    }
  },
  component: LoginPage,
})

let back = () => {
  let to: string | null = null
  try { to = localStorage.getItem('back'); localStorage.removeItem('back') } catch { /* home */ }
  return to?.startsWith('/') && !to.startsWith('//') ? to : '/'
}

function LoginPage() {
  let trpc = useTRPC()
  let search = useSearch({ strict: false }) as { token?: string }
  let isDev = import.meta.env.DEV

  let sendInvitation = useMutation(trpc.auth.sendInvitation.mutationOptions())
  let devSignIn = useMutation(
    trpc.auth.devSignIn.mutationOptions({
      onSuccess() {
        window.location.href = back()
      }
    })
  )
  let verifyMagicLink = useMutation(
    trpc.auth.signIn.mutationOptions({
      onSuccess() {
        window.location.href = back()
      }
    })
  )

  let form = useForm({
    canSubmitWhenInvalid: true,
    defaultValues: {
      token: search.token || '',
      email: isDev ? 'alexei.selivanov@gmail.com' : ''
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        if (value.token) {
          await verifyMagicLink.mutateAsync({ token: value.token })
        } else if (value.email) {
          if (isDev) await devSignIn.mutateAsync({ email: value.email })
          else await sendInvitation.mutateAsync({ email: value.email, loginUrl: window.location.origin + '/login' })
        }
      }
    }
  })
  
  console.log('getAllErrors', form.getAllErrors())

  let token = useStore(form.store, (state) => state.values.token)
  useEffect(() => {
    if (token) {
      form.handleSubmit()
    }
  }, [token, form])

  let emailInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    setTimeout(() => emailInputRef.current?.focus(), 100)
  }, [])

  return (
    <SplashLayout>
      {sendInvitation.isSuccess ?
        <form.Subscribe selector={(state) => state.values.email}>
          {(email) => (
            <div class={$.success}>
              <p>
                Ссылка для входа отправлена&nbsp;на <b>{email}</b>.
              </p>
              <p class={$type.footnote}>Проверьте почту и&nbsp;перейдите по&nbsp;ссылке.</p>
              <p>
                <a href='/login' class={$type.underline}>
                  Войти с другим адресом
                </a>
              </p>
            </div>
          )}
        </form.Subscribe>
      : <form
          class={$.form}
          style={{ flex: 0 }}
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
        >
          <h3>Вход в личный кабинет</h3>

          <form.Field name='token'>{(field) => <Errors errors={field.state.meta.errors} />}</form.Field>

          {verifyMagicLink.isPending ?
            <div class={$.loading} style={{ fontSize: '2em' }}>
              <Spinner />
            </div>
          : <div class={$layout.col}>
              <form.Field name='email'>
                {(field) => (
                  <>
                    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                      <div
                        class={clsx($.field, { [$.hasError]: !field.state.meta.isValid && field.state.meta.isTouched })}
                      >
                        <input
                          ref={emailInputRef}
                          autoComplete='email'
                          required
                          type='email'
                          pattern="(?!\.)(?!.*\.\.)([a-z0-9_'+\-\.]*)[a-z0-9_+\-]@([a-z0-9][a-z0-9\-]*\.)+[a-z]{2,}"
                          value={field.state.value}
                          onChange={(e) => {
                            field.handleChange(e.target.value)
                            form.resetField('token')
                          }}
                          onBlur={field.handleBlur}
                          disabled={sendInvitation.isPending || devSignIn.isPending}
                          class={$.input}
                          onInput={(event) => {
                            let input = event.target as HTMLInputElement
                            input.setCustomValidity(
                              input.validity.patternMismatch ? 'Неправильный адрес электронной почты.' : ''
                            )
                            let fontSize = 18
                            input.style.fontSize = `${fontSize}px`
                            while (input.scrollWidth > input.clientWidth + 1 && fontSize > 12) {
                              fontSize--
                              input.style.fontSize = `${fontSize}px`
                            }
                          }}
                          placeholder='Электронная почта'
                        />
                        <form.Subscribe selector={(state) => state.isSubmitting}>
                          {(isSubmitting) => (
                            <button
                              class={`plain ${$.button} ${sendInvitation.isPending || devSignIn.isPending ? $anim.pulse : ''}`}
                              type='submit'
                              disabled={isSubmitting}
                              title={isDev ? 'Войти в development-окружение' : 'Отправить ссылку для входа'}
                            >
                              <ArrowRightCircleFillIcon style={{ height: '32px', width: 'auto' }} />
                            </button>
                          )}
                        </form.Subscribe>
                      </div>
                      {field.state.meta.isTouched && (
                        <Errors
                          errors={field.state.meta.errors}
                          format={{
                            invalid_format: 'Неправильный адрес электронной почты.'
                          }}
                          class={clsx($.error, $type.small)}
                        />
                      )}
                    </div>
                    <p class={clsx($type.footnote, $type.small)}>
                      {isDev
                        ? 'Development-режим: вход без одноразовой ссылки.'
                        : 'Мы не используем пароли. Введите свой адрес электронной почты и мы отправим одноразовую ссылку для входа.'}
                    </p>
                  </>
                )}
              </form.Field>
            </div>
          }
        </form>
      }
    </SplashLayout>
  )
}
